import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { formatCurrency, formatDate } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiBase, getToken } from '@/lib/api';

const NOTE_KINDS = [
  { value: 'note', label: 'Note' },
  { value: 'called', label: 'Called Customer' },
  { value: 'emailed', label: 'Sent Email' },
  { value: 'promise_to_pay', label: 'Promise to Pay' },
];

function toYmd(d) {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function kindLabel(kind) {
  return NOTE_KINDS.find((k) => k.value === kind)?.label || String(kind || 'note').replace(/_/g, ' ');
}

export default function CollectionsPage() {
  const qc = useQueryClient();
  const [showLog, setShowLog] = useState(false);
  const [statementParty, setStatementParty] = useState('');
  const [form, setForm] = useState({
    invoice: '',
    kind: 'note',
    details: '',
    promiseDate: null,
    promiseAmount: '',
  });

  const dataQ = useQuery({
    queryKey: ['collections-page'],
    queryFn: async () => {
      const [q, p] = await Promise.all([
        api('/api/v1/collections/queue'),
        api('/api/v1/shippers'),
      ]);
      if (q.status !== 200) throw new Error(q.data?.error ?? 'Failed to load collections');
      if (p.status !== 200) throw new Error(p.data?.error ?? 'Failed to load parties');
      return {
        overdue: q.data.overdue ?? [],
        openInvoices: q.data.openInvoices ?? q.data.overdue ?? [],
        promises: q.data.promises ?? [],
        notes: q.data.notes ?? [],
        overdueTotalCents: q.data.overdueTotalCents ?? 0,
        parties: p.data.shippers ?? [],
      };
    },
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load collections');
  }, [dataQ.isError, dataQ.error]);

  const overdue = dataQ.data?.overdue ?? [];
  const openInvoices = dataQ.data?.openInvoices ?? overdue;
  const promises = dataQ.data?.promises ?? [];
  const notes = dataQ.data?.notes ?? [];
  const parties = dataQ.data?.parties ?? [];
  const overdueTotal = dataQ.data?.overdueTotalCents ?? 0;

  const invoiceLabel = useMemo(() => {
    const map = new Map();
    for (const o of openInvoices) map.set(o.id, o.invoiceNumber || o.id?.slice?.(0, 8));
    for (const o of overdue) map.set(o.id, o.invoiceNumber || o.id?.slice?.(0, 8));
    return map;
  }, [openInvoices, overdue]);

  const upsertNote = (note) => {
    if (!note) return;
    qc.setQueryData(['collections-page'], (prev) => {
      const base = prev ?? {
        overdue: [],
        openInvoices: [],
        promises: [],
        notes: [],
        overdueTotalCents: 0,
        parties: [],
      };
      const nextNotes = [note, ...(base.notes || []).filter((n) => n.id !== note.id)];
      const nextPromises =
        note.kind === 'promise_to_pay'
          ? [note, ...(base.promises || []).filter((n) => n.id !== note.id)]
          : base.promises || [];
      return { ...base, notes: nextNotes, promises: nextPromises };
    });
  };

  const handleLog = async () => {
    if (!form.invoice) {
      toast.error('Select an invoice');
      return;
    }
    if (!form.kind) {
      toast.error('Note type required');
      return;
    }
    const r = await api('/api/v1/collections/notes', {
      method: 'POST',
      body: {
        invoiceId: form.invoice,
        kind: form.kind,
        body: form.details.trim(),
        promiseDate:
          form.kind === 'promise_to_pay' ? toYmd(form.promiseDate) || undefined : undefined,
        promiseAmountCents:
          form.kind === 'promise_to_pay' && form.promiseAmount
            ? Math.round((parseFloat(form.promiseAmount) || 0) * 100)
            : undefined,
      },
    });
    if (r.status === 201) {
      toast.success('Note saved');
      upsertNote(r.data?.note);
      setForm({ invoice: '', kind: 'note', details: '', promiseDate: null, promiseAmount: '' });
      setShowLog(false);
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const overdueNotice = async (id) => {
    const r = await api('/api/v1/collections/overdue-notice', {
      method: 'POST',
      body: { invoiceId: id },
    });
    if (r.status === 201) {
      toast.success('Overdue notice logged');
      upsertNote(r.data?.note);
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const openStatement = () => {
    const party = parties.find((p) => p.id === statementParty);
    if (!party) {
      toast.error('Select a customer');
      return;
    }
    const token = getToken();
    const url = `${getApiBase()}/api/v1/collections/statement?billToId=${encodeURIComponent(party.id)}&billToName=${encodeURIComponent(party.name)}`;
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Statement failed');
        const html = await res.text();
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(html);
          w.document.close();
        }
      })
      .catch(() => toast.error('Statement failed'));
  };

  const overdueColumns = [
    { key: 'invoiceNumber', label: 'Invoice', className: 'font-mono' },
    { key: 'billToName', label: 'Customer', sortable: true },
    {
      key: 'balanceCents',
      label: 'Amount',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'daysPastDue',
      label: 'Days Past',
      className: 'font-mono text-destructive',
    },
    { key: 'bucket', label: 'Bucket' },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setForm((f) => ({ ...f, invoice: row.id }));
              setShowLog(true);
            }}
          >
            Note
          </Button>
          <Button size="sm" variant="ghost" onClick={() => overdueNotice(row.id)}>
            Notice
          </Button>
        </div>
      ),
    },
  ];

  const noteColumns = [
    {
      key: 'createdAt',
      label: 'When',
      render: (v) => formatDate(v),
    },
    {
      key: 'kind',
      label: 'Type',
      render: (v) => <StatusPill status={String(v || 'note')} />,
    },
    {
      key: 'invoiceId',
      label: 'Invoice',
      className: 'font-mono',
      render: (v) => invoiceLabel.get(v) || (v ? String(v).slice(0, 8) : '—'),
    },
    { key: 'billToName', label: 'Customer', render: (v) => v || '—' },
    {
      key: 'body',
      label: 'Details',
      render: (v, row) => (
        <div className="max-w-md">
          <p className="text-sm">{v || '—'}</p>
          {row.kind === 'promise_to_pay' ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              Promise {formatCurrency((row.promiseAmountCents ?? 0) / 100)} · {row.promiseDate || 'n/a'}
            </p>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Collections"
        description="Track overdue invoices and payment promises"
        icon="Megaphone"
        metrics={[
          { label: 'Overdue', value: overdue.length },
          { label: 'Due $', value: formatCurrency(overdueTotal / 100) },
          { label: 'Promises', value: promises.length },
          { label: 'Notes', value: notes.length },
        ]}
        actions={
          <Button onClick={() => setShowLog(!showLog)}>
            <Icons.Plus className="h-4 w-4 mr-2" />
            Log Note
          </Button>
        }
      />

      {showLog && (
        <Card>
          <CardHeader>
            <CardTitle>Log Collection Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Invoice *
                </label>
                <Combobox
                  options={openInvoices.map((o) => ({
                    value: o.id,
                    label: `${o.invoiceNumber || o.id?.slice?.(0, 8) || 'Invoice'} — ${o.billToName || 'Customer'}${
                      o.daysPastDue > 0 ? ` (${o.daysPastDue}d overdue)` : o.status === 'draft' ? ' (draft)' : ''
                    }`,
                  }))}
                  value={form.invoice}
                  onChange={(v) => setForm({ ...form, invoice: v })}
                  placeholder="Select invoice"
                  emptyMessage="No open invoices"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Kind *
                </label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select kind" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>
                        {k.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Details
              </label>
              <textarea
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                rows={3}
                placeholder={`Optional — defaults to “${kindLabel(form.kind)}” if blank`}
                className="mt-2 w-full px-3 py-2 border rounded-md text-sm resize-none outline-none focus:ring-2 focus:ring-ring bg-background"
              />
            </div>

            {form.kind === 'promise_to_pay' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Promise Date</label>
                  <DateField
                    value={form.promiseDate}
                    onChange={(d) => setForm({ ...form, promiseDate: d })}
                  />
                </div>
                <FloatingField
                  label="Promise Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.promiseAmount}
                  onChange={(e) => setForm({ ...form, promiseAmount: e.target.value })}
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLog(false)}>
                Cancel
              </Button>
              <Button onClick={handleLog}>Save Note</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Customer Statement</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1 w-full space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Customer
            </label>
            <Combobox
              options={parties.map((p) => ({ value: p.id, label: p.name }))}
              value={statementParty}
              onChange={setStatementParty}
              placeholder="Select customer"
            />
          </div>
          <Button variant="outline" onClick={openStatement}>
            <Icons.Printer className="h-4 w-4 mr-2" />
            Print Statement
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={noteColumns}
            data={notes}
            emptyMessage={dataQ.isLoading ? 'Loading…' : 'No collection notes yet'}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment Promises</CardTitle>
        </CardHeader>
        <CardContent>
          {promises.length > 0 ? (
            <div className="space-y-2">
              {promises.slice(0, 12).map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Icons.Handshake className="h-4 w-4 text-primary" />
                    <div>
                      <p className="font-medium text-sm">{p.billToName || 'Customer'}</p>
                      <p className="text-xs text-muted-foreground">{p.body}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium">
                      {formatCurrency((p.promiseAmountCents ?? 0) / 100)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Due {p.promiseDate || 'n/a'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No open promises</p>
          )}
        </CardContent>
      </Card>

      <DataTable
        columns={overdueColumns}
        data={overdue}
        emptyMessage={dataQ.isLoading ? 'Loading…' : 'No overdue invoices'}
      />
    </div>
  );
}
